import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './HotelRegistration.css';

const HotelRegistration = () => {
  const { currentUser, refreshHotelStatus, hotelStatus, hotelStatusLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Details
    ownerName: '',
    ownerEmail: currentUser?.email || '',
    ownerPhone: '',
    ownerId: '',
    idType: 'passport',
    
    // Hotel Details
    hotelName: '',
    hotelAddress: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    latitude: '',
    longitude: '',
    hotelCategory: '3-star',
    description: '',
    
    // Additional Info
    totalRooms: '',
    totalFloors: '',
    checkInTime: '14:00',
    checkOutTime: '11:00',
    
    // Documents
    ownershipProof: null,
    businessLicense: null,
    idDocument: null,
    hotelPhotos: []
  });

  const [errors, setErrors] = useState({});

  // Pre-populate form when editing existing registration
  useEffect(() => {
    if (!currentUser) return;
    if (hotelStatus && (hotelStatus.status === 'pending' || hotelStatus.status === 'declined')) {
      console.log('📝 Pre-populating form with existing registration data');
      setIsEditMode(true);
      setFormData(prev => ({
        ...prev,
        // Personal Details (from users and hotel_registrations)
        ownerName: hotelStatus.full_name || hotelStatus.owner_name || prev.ownerName,
        ownerEmail: hotelStatus.email || hotelStatus.owner_email || currentUser.email || prev.ownerEmail,
        ownerPhone: hotelStatus.owner_phone || prev.ownerPhone,
        ownerId: hotelStatus.owner_id_number || prev.ownerId,
        idType: hotelStatus.id_type || prev.idType,

        // Hotel Details
        hotelName: hotelStatus.hotel_name || prev.hotelName,
        hotelAddress: hotelStatus.address || prev.hotelAddress,
        city: hotelStatus.city || prev.city,
        state: hotelStatus.state || prev.state,
        country: hotelStatus.country || prev.country,
        zipCode: hotelStatus.zip_code || prev.zipCode,
        latitude: hotelStatus.latitude || prev.latitude,
        longitude: hotelStatus.longitude || prev.longitude,
        hotelCategory: hotelStatus.hotel_category || prev.hotelCategory,
        description: hotelStatus.description || prev.description,

        // Additional Info
        totalRooms: hotelStatus.total_rooms || prev.totalRooms,
        totalFloors: hotelStatus.total_floors || prev.totalFloors,
        checkInTime: hotelStatus.check_in_time || prev.checkInTime,
        checkOutTime: hotelStatus.check_out_time || prev.checkOutTime,

        // Documents (do not pre-populate file objects, but can show file names/links if needed)
        // ownershipProof, businessLicense, idDocument, hotelPhotos are not pre-populated as files
      }));
    }
    if (hotelStatus && hotelStatus.status === 'not_registered') {
      setIsEditMode(false);
      setFormData(prev => ({
        ...prev,
        ownerName: hotelStatus.full_name || hotelStatus.owner_name || prev.ownerName,
        ownerEmail: hotelStatus.email || hotelStatus.owner_email || currentUser.email || prev.ownerEmail,
        ownerPhone: hotelStatus.owner_phone || prev.ownerPhone,
      }));
    }
  }, [hotelStatus, currentUser]);

  const categories = [
    '1-star', '2-star', '3-star', '4-star', '5-star',
    'Boutique', 'Budget', 'Luxury', 'Resort', 'Hostel'
  ];

  const idTypes = [
    { value: 'passport', label: 'Passport' },
    { value: 'national_id', label: 'National ID' },
    { value: 'drivers_license', label: 'Driver\'s License' }
  ];

  const validateStep = (currentStep) => {
    const newErrors = {};
    if (currentStep === 1) {
    if (!formData.ownerName) newErrors.ownerName = 'Required';
    if (!formData.ownerPhone) newErrors.ownerPhone = 'Required';
    if (!formData.ownerId) newErrors.ownerId = 'Required';
    if (!formData.ownerEmail) {
      newErrors.ownerEmail = 'Required';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.ownerEmail)) {
      newErrors.ownerEmail = 'Invalid email address';
    }
  }

  if (currentStep === 2) {
    if (!formData.hotelName) newErrors.hotelName = 'Required';
    if (!formData.city) newErrors.city = 'Required';
    if (!formData.country) newErrors.country = 'Required';
    if (!formData.description) newErrors.description = 'Required';
  }
  if (currentStep === 3) {
    if (!formData.totalRooms) newErrors.totalRooms = 'Required';
    if (!formData.totalFloors) newErrors.totalFloors = 'Required';
  }
  if (currentStep === 4 && !isEditMode) {
    if (!formData.ownershipProof) newErrors.ownershipProof = 'Required';
    if (!formData.businessLicense) newErrors.businessLicense = 'Required';
    if (!formData.idDocument) newErrors.idDocument = 'Required';
    if (formData.hotelPhotos.length < 3) newErrors.hotelPhotos = 'Minimum 3 photos required';
  }
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (name === 'hotelPhotos') {
      // Handle multiple photos
      const filesArray = Array.from(files);
      if (formData.hotelPhotos.length + filesArray.length > 10) {
        toast.error('Maximum 10 photos allowed');
        return;
      }
      setFormData(prev => ({
        ...prev,
        hotelPhotos: [...prev.hotelPhotos, ...filesArray]
      }));
    } else {
      // Handle single file
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    }
  };

  // Step navigation functions (must be in component scope)
  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  if (hotelStatusLoading || !currentUser) {
    return (
      <div className="hotel-registration-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div className="spinner" style={{ fontSize: '1.5rem', color: '#888' }}>Loading...</div>
      </div>
    );
  }

  const removePhoto = (index) => {
  setFormData(prev => ({
    ...prev,
    hotelPhotos: prev.hotelPhotos.filter((_, i) => i !== index)
  }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(step)) {
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();
      
      // Append all text data
      Object.keys(formData).forEach(key => {
        if (formData[key] && typeof formData[key] !== 'object') {
          submitData.append(key, formData[key]);
        }
      });

      // Append files
      if (formData.ownershipProof) {
        submitData.append('ownershipProof', formData.ownershipProof);
      }
      if (formData.businessLicense) {
        submitData.append('businessLicense', formData.businessLicense);
      }
      if (formData.idDocument) {
        submitData.append('idDocument', formData.idDocument);
      }
      
      // Append hotel photos (all under the same field name for multer)
      formData.hotelPhotos.forEach((photo) => {
        submitData.append('hotelPhotos', photo);
      });

      const response = await fetch('/api/hotels/register', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        },
        body: submitData
      });

      const contentType = response.headers.get('content-type');
      let result;
      
      if (contentType && contentType.includes('application/json')) {
        result = await response.json();
      } else {
        // Handle non-JSON response (like proxy errors)
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Server error: ' + (text.substring(0, 100) || 'Unknown error'));
      }

      if (response.ok) {
        toast.success(isEditMode 
          ? 'Hotel registration updated successfully! Your changes are under review.' 
          : 'Hotel registration submitted successfully! Pending admin approval.');
        
        console.log('✅ Registration submitted successfully');
        
        // Wait for database to commit
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Refresh hotel status to update context with new registration
        console.log('🔄 Refreshing hotel status after submission...');
        await refreshHotelStatus();
        
        // Now navigate to status page with updated context
        console.log('🔀 Redirecting to status page...');
        navigate('/owner/status', { replace: true });
      } else {
        toast.error(result.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.message || 'Failed to submit registration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="step-indicator">
      <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
        <div className="step-number">1</div>
        <div className="step-label">Personal Info</div>
      </div>
      <div className={`step-line ${step > 1 ? 'completed' : ''}`}></div>
      <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
        <div className="step-number">2</div>
        <div className="step-label">Hotel Details</div>
      </div>
      <div className={`step-line ${step > 2 ? 'completed' : ''}`}></div>
      <div className={`step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}>
        <div className="step-number">3</div>
        <div className="step-label">Additional Info</div>
      </div>
      <div className={`step-line ${step > 3 ? 'completed' : ''}`}></div>
      <div className={`step ${step >= 4 ? 'active' : ''}`}>
        <div className="step-number">4</div>
        <div className="step-label">Documents</div>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="form-step">
      <h2>Personal Information</h2>
      <p className="step-description">Please provide your personal details for verification</p>

      <div className="form-row">
        <div className="form-group">
          <label>Full Name * <small>(minimum 3 characters)</small></label>
          <input
            type="text"
            name="ownerName"
            value={formData.ownerName}
            onChange={handleInputChange}
            placeholder="Enter your full name"
            className={errors.ownerName ? 'error' : ''}
            minLength={3}
          />
          {errors.ownerName && <span className="error-message">{errors.ownerName}</span>}
        </div>

        <div className="form-group">
          <label>Email Address *</label>
          <input
            type="email"
            name="ownerEmail"
            value={formData.ownerEmail}
            onChange={handleInputChange}
            placeholder="Enter your email"
            className={errors.ownerEmail ? 'error' : ''}
          />
          {errors.ownerEmail && (
            <span className="error-message">{errors.ownerEmail}</span>
          )}

        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Phone Number * <small>(at least 7 digits)</small></label>
          <input
            type="tel"
            name="ownerPhone"
            value={formData.ownerPhone}
            onChange={handleInputChange}
            placeholder="+1 (555) 123-4567"
            className={errors.ownerPhone ? 'error' : ''}
          />
          {errors.ownerPhone && <span className="error-message">{errors.ownerPhone}</span>}
        </div>

        <div className="form-group">
          <label>ID Type *</label>
          <select
            name="idType"
            value={formData.idType}
            onChange={handleInputChange}
          >
            {idTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-group">
        <label>ID Number *</label>
        <input
          type="text"
          name="ownerId"
          value={formData.ownerId}
          onChange={handleInputChange}
          placeholder="Enter your ID number"
          className={errors.ownerId ? 'error' : ''}
        />
        {errors.ownerId && <span className="error-message">{errors.ownerId}</span>}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="form-step">
      <h2>Hotel Details</h2>
      <p className="step-description">Provide information about your hotel property</p>

      <div className="form-group">
        <label>Hotel Name * <small>(minimum 3 characters)</small></label>
        <input
          type="text"
          name="hotelName"
          value={formData.hotelName}
          onChange={handleInputChange}
          placeholder="e.g., Grand Palace Hotel"
          className={errors.hotelName ? 'error' : ''}
          minLength={3}
        />
        {errors.hotelName && <span className="error-message">{errors.hotelName}</span>}
      </div>

      <div className="form-group">
        <label>Hotel Category *</label>
        <select
          name="hotelCategory"
          value={formData.hotelCategory}
          onChange={handleInputChange}
        >
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Address *</label>
        <input
          type="text"
          name="hotelAddress"
          value={formData.hotelAddress}
          onChange={handleInputChange}
          placeholder="Street address"
          className={errors.hotelAddress ? 'error' : ''}
        />
        {errors.hotelAddress && <span className="error-message">{errors.hotelAddress}</span>}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>City * <small>(minimum 2 characters)</small></label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleInputChange}
            placeholder="e.g., New York"
            className={errors.city ? 'error' : ''}
            minLength={2}
          />
          {errors.city && <span className="error-message">{errors.city}</span>}
        </div>

        <div className="form-group">
          <label>State/Province</label>
          <input
            type="text"
            name="state"
            value={formData.state}
            onChange={handleInputChange}
            placeholder="State or Province"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Country * <small>(minimum 2 characters)</small></label>
          <input
            type="text"
            name="country"
            value={formData.country}
            onChange={handleInputChange}
            placeholder="e.g., United States"
            className={errors.country ? 'error' : ''}
            minLength={2}
          />
          {errors.country && <span className="error-message">{errors.country}</span>}
        </div>

        <div className="form-group">
          <label>ZIP/Postal Code</label>
          <input
            type="text"
            name="zipCode"
            value={formData.zipCode}
            onChange={handleInputChange}
            placeholder="ZIP Code"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Latitude (Optional)</label>
          <input
            type="text"
            name="latitude"
            value={formData.latitude}
            onChange={handleInputChange}
            placeholder="40.7128"
          />
        </div>

        <div className="form-group">
          <label>Longitude (Optional)</label>
          <input
            type="text"
            name="longitude"
            value={formData.longitude}
            onChange={handleInputChange}
            placeholder="-74.0060"
          />
        </div>
      </div>

      <div className="form-group">
        <label>Hotel Description * <small>(minimum 10 characters)</small></label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          placeholder="Describe your hotel, amenities, and unique features... (minimum 10 characters)"
          rows="5"
          className={errors.description ? 'error' : ''}
          minLength={10}
        />
        {errors.description && <span className="error-message">{errors.description}</span>}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="form-step">
      <h2>Additional Information</h2>
      <p className="step-description">Provide operational details about your hotel</p>

      <div className="form-row">
        <div className="form-group">
          <label>Total Rooms *</label>
          <input
            type="number"
            name="totalRooms"
            value={formData.totalRooms}
            onChange={handleInputChange}
            placeholder="Number of rooms"
            min="1"
            className={errors.totalRooms ? 'error' : ''}
          />
          {errors.totalRooms && <span className="error-message">{errors.totalRooms}</span>}
        </div>

        <div className="form-group">
          <label>Total Floors *</label>
          <input
            type="number"
            name="totalFloors"
            value={formData.totalFloors}
            onChange={handleInputChange}
            placeholder="Number of floors"
            min="1"
            className={errors.totalFloors ? 'error' : ''}
          />
          {errors.totalFloors && <span className="error-message">{errors.totalFloors}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Check-in Time *</label>
          <input
            type="time"
            name="checkInTime"
            value={formData.checkInTime}
            onChange={handleInputChange}
          />
        </div>

        <div className="form-group">
          <label>Check-out Time *</label>
          <input
            type="time"
            name="checkOutTime"
            value={formData.checkOutTime}
            onChange={handleInputChange}
          />
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="form-step">
      <h2>Upload Documents</h2>
      <p className="step-description">Please upload required documents for verification</p>

      <div className="upload-section">
        <div className="upload-group">
          <label>Ownership Proof * (Deed, Title, etc.)</label>
          <div className="file-input-wrapper">
            <input
              type="file"
              name="ownershipProof"
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png"
              id="ownershipProof"
            />
            <label htmlFor="ownershipProof" className="file-input-label">
              {formData.ownershipProof ? formData.ownershipProof.name : 'Choose file'}
            </label>
          </div>
          {errors.ownershipProof && <span className="error-message">{errors.ownershipProof}</span>}
        </div>

        <div className="upload-group">
          <label>Business License *</label>
          <div className="file-input-wrapper">
            <input
              type="file"
              name="businessLicense"
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png"
              id="businessLicense"
            />
            <label htmlFor="businessLicense" className="file-input-label">
              {formData.businessLicense ? formData.businessLicense.name : 'Choose file'}
            </label>
          </div>
          {errors.businessLicense && <span className="error-message">{errors.businessLicense}</span>}
        </div>

        <div className="upload-group">
          <label>ID Document *</label>
          <div className="file-input-wrapper">
            <input
              type="file"
              name="idDocument"
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png"
              id="idDocument"
            />
            <label htmlFor="idDocument" className="file-input-label">
              {formData.idDocument ? formData.idDocument.name : 'Choose file'}
            </label>
          </div>
          {errors.idDocument && <span className="error-message">{errors.idDocument}</span>}
        </div>

        <div className="upload-group">
          <label>Hotel Photos * (Minimum 3, Maximum 10)</label>
          <div className="file-input-wrapper">
            <input
              type="file"
              name="hotelPhotos"
              onChange={handleFileChange}
              accept="image/*"
              multiple
              id="hotelPhotos"
            />
            <label htmlFor="hotelPhotos" className="file-input-label">
              Add photos ({formData.hotelPhotos.length}/10)
            </label>
          </div>
          {errors.hotelPhotos && <span className="error-message">{errors.hotelPhotos}</span>}
          
          {formData.hotelPhotos.length > 0 && (
            <div className="photo-preview-grid">
              {formData.hotelPhotos.map((photo, index) => (
                <div key={index} className="photo-preview">
                  <img src={URL.createObjectURL(photo)} alt={`Hotel ${index + 1}`} />
                  <button 
                    type="button" 
                    className="remove-photo"
                    onClick={() => removePhoto(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <strong>📄 Document Requirements:</strong>
        <ul>
          <li>All documents must be clear and legible</li>
          <li>Accepted formats: PDF, JPG, PNG</li>
          <li>Maximum file size: 5MB per document</li>
          <li>Hotel photos should showcase exterior, lobby, and rooms</li>
        </ul>
      </div>
    </div>
  );

  const handleInputChange = (e) => {
  const { name, value } = e.target;
  setFormData(prev => ({
    ...prev,
    [name]: value
  }));
  };


  return (
    <div className="hotel-registration-container">
      <button 
        className="back-button"
        onClick={() => {
          // Always go to dashboard, regardless of status
          console.log('Back to Dashboard button pressed'); 
          navigate('/owner');
        }}
        style={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          background: 'rgba(255, 255, 255, 0.9)',
          border: 'none',
          padding: '10px 20px',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.2s',
          zIndex: 10
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'translateX(-4px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'translateX(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
        }}
      >
        ← Back to Dashboard
      </button>
      <div className="registration-header">
        <h1>{isEditMode ? 'Edit Hotel Registration' : 'Hotel Registration'}</h1>
        <p>{isEditMode ? 'Update your hotel property information' : 'Register your hotel property with InnBotics'}</p>
        {isEditMode && hotelStatus?.status === 'declined' && (
          <div style={{ 
            background: '#fef3c7', 
            border: '1px solid #f59e0b', 
            borderRadius: '8px', 
            padding: '12px', 
            marginTop: '12px',
            fontSize: '14px'
          }}>
            <strong>⚠️ Previous submission was declined:</strong>
            <p style={{ margin: '4px 0 0 0', color: '#92400e' }}>
              {hotelStatus.decline_reason || hotelStatus.declineReason}
            </p>
          </div>
        )}
      </div>

      {renderStepIndicator()}

      <form onSubmit={handleSubmit} className="registration-form">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}

        <div className="form-actions">
          {step > 1 && (
            <button
              type="button"
              onClick={prevStep}
              className="btn-secondary"
              disabled={loading}
            >
              Previous
            </button>
          )}
          
          {step < 4 ? (
            <button
              type="button"
              onClick={nextStep}
              className="btn-primary"
            >
              Next
            </button>
          ) : (
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? (isEditMode ? 'Updating...' : 'Submitting...') : (isEditMode ? 'Update Registration' : 'Submit Registration')}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default HotelRegistration;
